// ============================================================================
// MarketOperatorRequestCard — заявка в операторском экране (возврат/архив).
// Без таблиц и модальных окон: карточка, статус и встроенная форма возврата.
// ============================================================================

import { useState } from 'react';
import {
  Archive,
  CheckCircle2,
  ClipboardList,
  Loader2,
  MapPin,
  Undo2,
  UserRound,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatValue } from '@/lib/format';
import { marketRequestTitle } from '@/lib/market';
import type { MarketOperatorRequest } from '@/lib/marketOperator';
import { MarketStatusChip } from './MarketStatusChip';
import { MarketRatingBadge } from './MarketRatingBadge';

interface MarketOperatorRequestCardProps {
  request: MarketOperatorRequest;
  mode: 'refund' | 'archive';
  busy?: boolean;
  refunded?: boolean;
  /** Возвращает true, если возврат принят сервером (форма закроется). */
  onRefund?: (request: MarketOperatorRequest, reason: string, comment: string) => Promise<boolean> | boolean;
}

export function MarketOperatorRequestCard({
  request,
  mode,
  busy = false,
  refunded = false,
  onRefund,
}: MarketOperatorRequestCardProps) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');

  const created = formatValue(request.createdAt, 'datetime:dd.MM.yyyy HH:mm', 'timestamp');
  const updated = formatValue(request.updatedAt, 'datetime:dd.MM.yyyy HH:mm', 'timestamp');
  const title = marketRequestTitle(request);
  const price = request.takePriceValue;

  const submit = async () => {
    if (!onRefund) return;
    const accepted = await onRefund(request, reason.trim(), comment.trim());
    if (accepted) {
      setOpen(false);
      setReason('');
      setComment('');
    }
  };

  const canSubmit = reason.trim().length > 0 && comment.trim().length > 0 && !busy && !refunded;

  return (
    <article className="rounded-2xl border bg-card p-4 shadow-sm">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="line-clamp-2 break-words text-sm font-semibold leading-5">
            {title}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
            {request.categoryName && (
              <span className="rounded-full bg-muted px-2 py-0.5">{request.categoryName}</span>
            )}
            {created && <span>{created}</span>}
            {mode === 'archive' && updated && <span>Изменена: {updated}</span>}
          </div>
        </div>
        <MarketStatusChip status={request.status} label={request.statusLabel} />
      </div>

      {request.descr && (
        <p className="mt-2 line-clamp-3 whitespace-pre-wrap break-words text-xs leading-5 text-muted-foreground">
          {request.descr}
        </p>
      )}

      <div className="mt-2 space-y-1 text-xs text-muted-foreground">
        {request.address && (
          <div className="flex items-start gap-1.5">
            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span className="line-clamp-2">{request.address}</span>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          <UserRound className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">
            {request.masterName ? `Мастер: ${request.masterName}` : 'Мастер не выбран'}
          </span>
          {(request.masterName || request.masterCatalogId) && (
            <MarketRatingBadge
              rating={request.masterRating}
              count={request.masterReviewsCount}
              compact
            />
          )}
        </div>
        {mode === 'refund' && price !== null && (
          <div className="flex items-center gap-1.5">
            <ClipboardList className="h-3.5 w-3.5 shrink-0" />
            <span>Стоимость взятия: {price.toLocaleString('ru-RU')}</span>
          </div>
        )}
      </div>

      {mode === 'archive' ? (
        <div className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Archive className="h-3.5 w-3.5" />
          Заявка в архиве — действия недоступны, история сохранена.
        </div>
      ) : refunded ? (
        <div className="mt-3 flex items-center gap-1.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-700">
          <CheckCircle2 className="h-4 w-4" />
          Возврат оформлен. Повторный запрос сервер не зачислит дважды.
        </div>
      ) : open ? (
        <div className="mt-3 space-y-2">
          <label className="block text-[11px] font-medium text-muted-foreground" htmlFor={`refund-reason-${request.id}`}>
            Причина возврата <span className="text-destructive">*</span>
          </label>
          <textarea
            id={`refund-reason-${request.id}`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Почему возвращаем деньги мастеру"
            className="w-full resize-y rounded-xl border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
            disabled={busy}
          />
          <label className="block text-[11px] font-medium text-muted-foreground" htmlFor={`refund-comment-${request.id}`}>
            Комментарий <span className="text-destructive">*</span>
          </label>
          <textarea
            id={`refund-comment-${request.id}`}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            placeholder="Что проверили и как приняли решение"
            className="w-full resize-y rounded-xl border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
            disabled={busy}
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-10 flex-1 rounded-xl"
              onClick={() => setOpen(false)}
              disabled={busy}
            >
              Отмена
            </Button>
            <Button
              type="button"
              className="h-10 flex-1 rounded-xl"
              onClick={() => void submit()}
              disabled={!canSubmit}
            >
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Undo2 className="mr-1.5 h-4 w-4" />}
              Вернуть
            </Button>
          </div>
          <div className="text-[11px] leading-4 text-muted-foreground">
            Сумма определяется сервером по фактическому списанию; повторный возврат не удваивается.
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          className="mt-3 h-10 w-full rounded-xl"
          onClick={() => setOpen(true)}
        >
          <Undo2 className="mr-1.5 h-4 w-4" />
          Вернуть стоимость взятия
        </Button>
      )}
    </article>
  );
}
